import React, { useState, useEffect } from 'react';
import { CORE_STAFF_ROLES } from '../config/roleConfig';
import { useGuestReviewCounts } from '../hooks/useGuestReviews';
import { useComplaintCounts } from '../hooks/useComplaints';
import { formatCount } from '../utils/numberFormatter';
import '../styles/navBadge.css';
import { Link, useLocation } from 'react-router-dom';
import './MobileNavigation.css';
import hostMeLogo from '../assets/images/branding/Hostme1.png';
import { getNavigationConfig, getRoleDisplayName } from '../config/navigation';

const MobileNavigation = ({ user, activeNavItem, setActiveNavItem, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use centralized navigation config - same as desktop
  const navigationItems = getNavigationConfig(user?.role);
  // «التقييمات والشكاوى» — ONE counter for the page's three tabs: reviews waiting (post-checkout review O9)
  // plus complaints needing action (tasks/complaints-to-fixes.md C15, C19). Fetched only for the roles that
  // can open the page.
  const isCoreStaff = CORE_STAFF_ROLES.includes(user?.role);
  const { data: reviewCounts } = useGuestReviewCounts(isCoreStaff);
  const { data: complaintCounts } = useComplaintCounts(isCoreStaff);
  const badges = { guestReviews: (reviewCounts?.badge || 0) + (complaintCounts?.badge || 0) };
  const badgeFor = (item) => badges[item.badge] || 0;
  const menuWaiting = navigationItems.reduce((sum, item) => sum + badgeFor(item), 0);

  // Get page title based on active navigation item
  const getPageTitle = (activeItemId) => {
    // Find the title from navigation config
    const navItem = navigationItems.find(item => item.id === activeItemId);
    return navItem ? navItem.label : 'هوست مي';
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSidebarOpen && !event.target.closest('.mobile-sidebar') && !event.target.closest('.sidebar-toggle')) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  // Handle navigation item click
  const handleNavClick = (item) => {
    setActiveNavItem(item.id);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Get active item based on current path
  const getActiveItemId = () => {
    const currentPath = location.pathname;
    const item = navigationItems.find(item => item.path === currentPath);
    return item ? item.id : activeNavItem;
  };

  const currentActiveItem = getActiveItemId();

  // Only render mobile navigation components when on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <button
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={menuWaiting > 0 ? `قائمة التنقل — ${menuWaiting} بانتظار الإجراء` : 'قائمة التنقل'}
          >
            <i className="bi bi-list"></i>
            {menuWaiting > 0 && <span className="nav-count-dot" aria-hidden="true"></span>}
          </button>
          
          <div className="mobile-brand">
            {currentActiveItem === 'overview' ? (
              // Overview page: Logo only, bigger size
              <img src={hostMeLogo} alt="Host Me" className="brand-logo overview-logo" />
            ) : (
              // Other pages: Title only, no logo
              <span className="page-title">{getPageTitle(currentActiveItem)}</span>
            )}
          </div>
          
          <button 
            className="mobile-logout"
            onClick={onLogout}
            aria-label="تسجيل الخروج"
          >
            <i className="bi bi-box-arrow-left"></i>
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}

      {/* Mobile Sidebar */}
      <nav className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              <i className="bi bi-person-circle"></i>
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'مستخدم'}</div>
              <div className="user-role">
                {getRoleDisplayName(user?.role)}
              </div>
            </div>
          </div>
          <button 
            className="sidebar-close"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="إغلاق القائمة"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        <div className="sidebar-nav">
          {navigationItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`sidebar-nav-item ${currentActiveItem === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item)}
            >
              <i className={`bi ${item.icon}`}></i>
              <span>{item.label}</span>
              {badgeFor(item) > 0 && (
                <span className="nav-count-badge" aria-label={`${badgeFor(item)} بانتظار الإجراء`}>
                  {formatCount(badgeFor(item))}
                </span>
              )}
            </Link>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="sidebar-logout" onClick={onLogout}>
            <i className="bi bi-box-arrow-right"></i>
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </nav>

    </>
  );
};

export default MobileNavigation;
